import { PublicClientApplication, type AccountInfo, type Configuration } from '@azure/msal-browser'
import { IS_DEMO } from '../api/demo'

/**
 * Entra sign-in for the SPA.
 *
 * No client secret exists here, and none is needed: this is a public client
 * using authorization-code flow with PKCE. Anything embedded in a browser bundle
 * is readable by anyone who opens devtools, so the design is that there is
 * nothing worth reading.
 */

/**
 * Reads a required Vite variable, or fails HERE with a message that names it.
 *
 * The fallbacks these replaced (`?? ''`, `?? 'common'`) turned a missing
 * .env.local into an empty client_id sent to Microsoft, which came back as
 * "AADSTS900144: The request body must contain the following parameter:
 * 'client_id'" — an error page that looks like a tenant problem and is read as
 * one. Observed 4 Aug 2026 on the first live sign-in attempt.
 *
 * A default that lets a misconfigured app reach an identity provider is worse
 * than no default: it moves the diagnosis to somebody else's server.
 */
function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. Copy web/.env.example to web/.env.local and fill it in ` +
        `from the Entra app registration, then restart the dev server — Vite reads ` +
        `env files only at startup.`,
    )
  }
  return value
}

/**
 * Where Microsoft's sign-in page is asked for.
 *
 * VITE_ENTRA_SIGN_IN_TENANT is 'organizations' in production (owner, 4 Nov
 * 2026): people from REDA Chemicals, Mindzer and Optidor sign in, each in their
 * own company's directory, and a REDA Chemicals-only page refused the other two
 * with AADSTS50020. 'organizations' means WORK accounts from any company reach
 * Microsoft's page; which companies get IN is the API's list
 * (Entra:OtherTenantIds), checked on every call. Unset, it is the home tenant,
 * as before.
 *
 * Never 'common' or 'consumers': those also offer personal Microsoft accounts
 * (Outlook, Hotmail), which no company vouches for. Refused here, by name.
 */
function signInTenant(): string {
  const chosen =
    import.meta.env.VITE_ENTRA_SIGN_IN_TENANT ||
    required('VITE_ENTRA_TENANT_ID', import.meta.env.VITE_ENTRA_TENANT_ID)
  if (/^(common|consumers)$/i.test(chosen.trim())) {
    throw new Error(
      `VITE_ENTRA_SIGN_IN_TENANT is '${chosen}', which offers personal Microsoft accounts. ` +
        `Use 'organizations' or a tenant ID.`,
    )
  }
  return chosen.trim()
}

/**
 * Built on first use, NOT at module load. Demo mode has no Entra configuration
 * and is not supposed to need any; throwing at import time would break the one
 * mode that exists so the platform can be reviewed without a tenant.
 */
function entraConfig(): Configuration {
  return {
    auth: {
      clientId: required('VITE_ENTRA_CLIENT_ID', import.meta.env.VITE_ENTRA_CLIENT_ID),
      // No 'common' fallback, and no 'common' at all: see signInTenant.
      authority: 'https://login.microsoftonline.com/' + signInTenant(),
      // Must match an SPA redirect URI on the registration exactly, including
      // the port: http://localhost:5273 in dev (see vite.config.ts).
      redirectUri: window.location.origin,
    },
    cache: {
      // sessionStorage, not localStorage: tokens die with the tab. On a shared
      // workstation — which a finance floor has — localStorage would leave a
      // usable token behind for the next person to sit down.
      cacheLocation: 'sessionStorage',
    },
  }
}

const apiScope = () => required('VITE_ENTRA_API_SCOPE', import.meta.env.VITE_ENTRA_API_SCOPE)

let msal: PublicClientApplication | null = null

/** How long the redirect handshake gets before we treat it as stuck. */
const HANDSHAKE_TIMEOUT_MS = 10_000

/**
 * Clears MSAL's "an interaction is in progress" lock.
 *
 * MSAL sets this when loginRedirect() navigates away, and clears it when the
 * browser comes BACK through the redirect URI. If the user never comes back —
 * Entra showed an error, they closed the tab at the Microsoft page, the network
 * dropped mid-redirect — the lock survives in sessionStorage, and the next load
 * of the app waits on handleRedirectPromise() for an interaction that already
 * ended somewhere else. That wait never times out on its own.
 *
 * Only the lock is removed, not the token cache: a valid session in this tab
 * must survive a stuck handshake.
 */
function clearStaleInteractionLock(): void {
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i)
      if (k && k.startsWith('msal.') && k.includes('interaction.status')) keys.push(k)
    }
    keys.forEach((k) => sessionStorage.removeItem(k))
  } catch {
    /* Storage can be blocked entirely by policy. Nothing to clear, nothing to do. */
  }
}

/**
 * Turns an Entra sign-in failure into something the person reading it can act on.
 *
 * WHY THIS IS NOT COSMETIC
 * ------------------------
 * Every one of these is a CONFIGURATION problem on the app registration, and the
 * raw message says so in a way that reads like a fault in the platform. Observed
 * 4 Aug 2026: "invalid_client: AADSTS65005: The application 'api://f5ff1160-…'
 * asked for scope 'access_as_user' that doesn't exist" under a heading of
 * "Something went wrong" — which sends the reader looking at the wrong thing,
 * usually the code, sometimes for hours.
 *
 * The Microsoft text is kept underneath rather than replaced, because the trace
 * and correlation ids in it are what Microsoft support asks for.
 */
export function describeAuthError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);

  const guidance =
    /AADSTS65005/.test(raw)
      ? 'The app registration has no scope by the name this app asks for. ' +
        'In Entra > App registrations > MBI > Expose an API, add a scope named ' +
        'access_as_user and set its state to Enabled. If a scope already exists there under ' +
        'a different name, that name goes in web/.env.local as VITE_ENTRA_API_SCOPE instead.'
      : /AADSTS650053/.test(raw)
      ? 'The scope name does not match the one defined on the app registration. ' +
        'Copy the exact scope value from Expose an API into web/.env.local.'
      : /AADSTS9002326|cross-origin/i.test(raw)
      ? 'The redirect URI is registered under the wrong platform. In Entra > Authentication, ' +
        'http://localhost:5273 must sit under "Single-page application", not "Web". ' +
        'An entry cannot be moved between platforms — delete it and add it again.'
      : /AADSTS50011|redirect_uri/i.test(raw)
      ? 'This address is not a registered redirect URI. Add http://localhost:5273 under ' +
        'Entra > Authentication > Single-page application.'
      : /AADSTS65001|consent/i.test(raw)
      ? 'Nobody has consented to the API permission yet. In Entra > API permissions, add the ' +
        'delegated permission from My APIs, then use Grant admin consent.'
      : /AADSTS50194/.test(raw)
      ? 'This app signs in work accounts from more than one company, and the app registration ' +
        'still accepts only its own. In Entra > App registrations > MBI > Authentication, set ' +
        'Supported account types to "Accounts in any organizational directory (Multitenant)".'
      : /AADSTS700016|AADSTS900144/.test(raw)
      ? 'The client id sent to Microsoft was empty or unknown. Check VITE_ENTRA_CLIENT_ID in ' +
        'web/.env.local, and restart the dev server — Vite reads env files only at startup.'
      : '';

  return guidance ? `${guidance}\n\nMicrosoft reported: ${raw}` : raw;
}

export async function initAuth(): Promise<AccountInfo | null> {
  if (IS_DEMO) return null

  msal = new PublicClientApplication(entraConfig())
  await msal.initialize()

  // Completes a redirect sign-in if we have just come back from Entra.
  //
  // BOUNDED, because this call can wait for ever — see clearStaleInteractionLock.
  // An unbounded await here left the app on "Starting MBI…" with no
  // error and no way forward, which is the worst failure mode available: it
  // looks like slowness, so people wait rather than report it.
  let result: Awaited<ReturnType<PublicClientApplication['handleRedirectPromise']>> = null
  try {
    result = await Promise.race([
      msal.handleRedirectPromise(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('handshake-timeout')), HANDSHAKE_TIMEOUT_MS),
      ),
    ])
  } catch (e: unknown) {
    if (e instanceof Error && e.message === 'handshake-timeout') {
      // Recover rather than fail: drop the stale lock and carry on as a signed-
      // out user, so the next click on Sign in starts a clean interaction.
      // eslint-disable-next-line no-console
      console.warn(
        'MSAL redirect handshake did not complete within ' +
          `${HANDSHAKE_TIMEOUT_MS}ms — clearing a stale interaction lock and continuing signed out.`,
      )
      clearStaleInteractionLock()
      return null
    }
    throw e
  }

  if (result?.account) {
    msal.setActiveAccount(result.account)
    return result.account
  }

  const existing = msal.getAllAccounts()[0] ?? null
  if (existing) msal.setActiveAccount(existing)
  return existing
}

export async function signIn(): Promise<void> {
  if (!msal) throw new Error('Auth not initialised.')
  // Redirect rather than popup: popups are blocked by default in several
  // managed-browser configurations, which on a locked-down corporate desktop
  // means sign-in silently does nothing.
  //
  // prompt: 'select_account' is load-bearing on a SHARED desktop. sessionStorage
  // dies with the tab, but Entra's SSO cookie does not — so without this, User B
  // clicking "Continue with Microsoft" after User A closed the tab (without
  // signing out) would be silently re-signed AS User A, with no picker, breaking
  // the per-person attribution this platform depends on. Forcing the account
  // chooser makes a shared workstation always ask who is signing in.
  await msal.loginRedirect({ scopes: [apiScope()], prompt: 'select_account' })
}

export async function signOut(): Promise<void> {
  if (!msal) return
  await msal.logoutRedirect()
}

/**
 * A token for the API, refreshed silently where possible.
 *
 * Returns null rather than throwing when interaction is required, so the caller
 * renders a sign-in prompt instead of an error. An expired session is a normal
 * event, not a fault.
 */
export const getApiToken = async (): Promise<string | null> => {
  if (IS_DEMO) return 'demo'
  if (!msal) return null

  const account = msal.getActiveAccount()
  if (!account) return null

  try {
    const result = await msal.acquireTokenSilent({ scopes: [apiScope()], account })
    return result.accessToken
  } catch {
    return null
  }
}

export function activeAccount(): AccountInfo | null {
  return msal?.getActiveAccount() ?? null
}
